import React, { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar.jsx";
import "./FoyerIA.css";

const STATUS_LABELS = {
  all: "Tous",
  processed: "Traités",
  processing: "En cours",
  pending: "En attente",
  failed: "Échoués"
};

const STATUS_COLORS = {
  processed: "var(--accent-lime)",
  processing: "var(--accent)",
  pending: "var(--accent-warm)",
  failed: "#ff5555"
};

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
}

function getDocumentTypeIcon(type) {
  const icons = {
    facture: "📄",
    avis_impot: "📋",
    attestation: "📜",
    contrat: "📝",
    courrier: "✉️",
    releve: "📊",
    autre: "📎"
  };
  return icons[type] || "📎";
}

function getDocumentTypeLabel(type) {
  const labels = {
    facture: "Facture",
    avis_impot: "Avis d'impôt",
    attestation: "Attestation",
    contrat: "Contrat",
    courrier: "Courrier",
    releve: "Relevé",
    autre: "Autre"
  };
  return labels[type] || "Document";
}

export default function FoyerIA() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stats, setStats] = useState(null);

  // Charger les emails
  const loadEmails = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }
      const res = await fetch(`/api/fast/foyeria/emails?${params.toString()}`);
      const data = await res.json();
      if (data.ok) {
        setEmails(data.emails || []);
      } else {
        setError(data.error || "Erreur lors du chargement");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  // Charger les stats
  const loadStats = async () => {
    try {
      const res = await fetch("/api/fast/foyeria/stats");
      const data = await res.json();
      if (data) {
        setStats(data);
      }
    } catch (err) {
      console.error("Erreur chargement stats:", err);
    }
  };

  useEffect(() => {
    loadEmails();
    loadStats();
  }, [statusFilter]);

  // Ouvrir le détail d'un email
  const openDetail = (email) => {
    setSelectedEmail(email);
    setDetailOpen(true);
  };

  // Fermer le détail
  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedEmail(null);
  };

  // Télécharger un document
  const downloadDocument = async (doc) => {
    try {
      const res = await fetch(`/api/fast/foyeria/documents/${doc.document_id}/download`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.title || "document";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (err) {
      console.error("Erreur téléchargement:", err);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="page foyeria-page app-main">
        {/* Header */}
        <header className="foyeria-header">
          <div className="foyeria-title">
            <span className="foyeria-icon">📬</span>
            <h1 className="page-title-accent">FoyerIA - Boîte de réception</h1>
          </div>
          <p className="foyeria-subtitle">
            Emails traités automatiquement avec leurs documents
          </p>
        </header>

        {/* Stats */}
        {stats && (
          <div className="foyeria-stats">
            <div className="stat-card">
              <span className="stat-value">{stats.emails_processed || 0}</span>
              <span className="stat-label">Emails traités</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.total_documents || 0}</span>
              <span className="stat-label">Documents</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.letters_generated || 0}</span>
              <span className="stat-label">Courriers générés</span>
            </div>
          </div>
        )}

        {/* Filtres */}
        <div className="foyeria-filters">
          <div className="filter-group">
            <label>Statut :</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <button className="btn-refresh" onClick={loadEmails}>
            🔄 Rafraîchir
          </button>
        </div>

        {/* Liste des emails */}
        <div className="foyeria-content">
          {loading && (
            <div className="foyeria-loading">
              <div className="spinner"></div>
              <p>Chargement des emails...</p>
            </div>
          )}

          {error && (
            <div className="foyeria-error">
              <p>❌ {error}</p>
              <button onClick={loadEmails}>Réessayer</button>
            </div>
          )}

          {!loading && !error && emails.length === 0 && (
            <div className="foyeria-empty">
              <span className="empty-icon">📭</span>
              <p>Aucun email traité pour le moment</p>
              <p className="empty-hint">
                Les emails avec pièces jointes envoyés à votre adresse FoyerIA
                apparaîtront ici automatiquement.
              </p>
            </div>
          )}

          {!loading && !error && emails.length > 0 && (
            <div className="email-list">
              {emails.map((email) => (
                <div
                  key={email.email_id}
                  className="email-card"
                  onClick={() => openDetail(email)}
                >
                  <div className="email-header">
                    <div className="email-sender">
                      <span className="sender-avatar">
                        {(email.sender_name || email.sender || "?")[0].toUpperCase()}
                      </span>
                      <div className="sender-info">
                        <span className="sender-name">
                          {email.sender_name || email.sender}
                        </span>
                        <span className="sender-email">{email.sender}</span>
                      </div>
                    </div>
                    <div
                      className="email-status"
                      style={{ color: STATUS_COLORS[email.status] || "#888" }}
                    >
                      {STATUS_LABELS[email.status] || email.status}
                    </div>
                  </div>

                  <div className="email-subject">{email.subject}</div>

                  <div className="email-meta">
                    <span className="email-date">
                      📅 {formatDate(email.processed_at || email.received_at)}
                    </span>
                    <span className="email-docs">
                      📎 {email.documents?.length || 0} document(s)
                    </span>
                  </div>

                  {email.documents && email.documents.length > 0 && (
                    <div className="email-docs-preview">
                      {email.documents.slice(0, 3).map((doc) => (
                        <span key={doc.document_id} className="doc-chip">
                          {getDocumentTypeIcon(doc.type_document)}
                          {doc.title || "Document"}
                        </span>
                      ))}
                      {email.documents.length > 3 && (
                        <span className="doc-chip more">
                          +{email.documents.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal détail */}
        {detailOpen && selectedEmail && (
          <div className="foyeria-modal-overlay" onClick={closeDetail}>
            <div
              className="foyeria-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{selectedEmail.subject}</h2>
                <button className="modal-close" onClick={closeDetail}>
                  ✕
                </button>
              </div>

              <div className="modal-content">
                <div className="detail-section">
                  <h3>Informations</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Expéditeur</label>
                      <span>{selectedEmail.sender_name || selectedEmail.sender}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <span>{selectedEmail.sender}</span>
                    </div>
                    <div className="detail-item">
                      <label>Reçu le</label>
                      <span>{formatDate(selectedEmail.received_at)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Traité le</label>
                      <span>{formatDate(selectedEmail.processed_at)}</span>
                    </div>
                    <div className="detail-item">
                      <label>Statut</label>
                      <span style={{ color: STATUS_COLORS[selectedEmail.status] }}>
                        {STATUS_LABELS[selectedEmail.status] || selectedEmail.status}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>Documents ({selectedEmail.documents?.length || 0})</h3>
                  {selectedEmail.documents && selectedEmail.documents.length > 0 ? (
                    <div className="documents-list">
                      {selectedEmail.documents.map((doc) => (
                        <div key={doc.document_id} className="document-card">
                          <div className="doc-icon">
                            {getDocumentTypeIcon(doc.type_document)}
                          </div>
                          <div className="doc-info">
                            <span className="doc-title">{doc.title || "Document"}</span>
                            <span className="doc-type">
                              {getDocumentTypeLabel(doc.type_document)}
                            </span>
                            {doc.resume_court && (
                              <p className="doc-summary">{doc.resume_court}</p>
                            )}
                            {doc.montant_total && (
                              <span className="doc-amount">
                                💰 {doc.montant_total.toFixed(2)} €
                              </span>
                            )}
                            {doc.mots_cles && doc.mots_cles.length > 0 && (
                              <div className="doc-keywords">
                                {doc.mots_cles.slice(0, 5).map((kw, i) => (
                                  <span key={i} className="keyword-chip">{kw}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            className="doc-download"
                            onClick={() => downloadDocument(doc)}
                          >
                            ⬇️
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-docs">Aucun document associé</p>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={closeDetail}>
                  Fermer
                </button>
                <button className="btn-primary">
                  ✉️ Générer un courrier
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
